import { existsSync, readdirSync, statSync } from 'fs';
import { join, extname } from 'path';
import express from 'express';

interface DevConfig {
  appDir: string;
  port: number;
  host: string;
}

export async function startDevServer(config: DevConfig) {
  const { appDir, port, host } = config;

  console.log('🚀 Iniciando servidor de desarrollo rowiejs...');

  // Verificar que existe la carpeta app
  if (!existsSync(appDir)) {
    console.error(`❌ No se encontró la carpeta ${appDir}`);
    process.exit(1);
  }

  // Escanear rutas una vez al inicio
  console.log('📁 Escaneando rutas en:', appDir);
  const routes = scanRoutesSync(appDir);
  console.log(`🔍 Rutas encontradas:`, routes.map(r => r.path));

  // Crear servidor Express
  const app = express();

  // Middleware para logging
  app.use((req, _res, next) => {
    console.log(`📍 ${req.method} ${req.url}`);
    next();
  });

  // Middleware para manejar rutas de la aplicación
  app.use(async (req, res) => {
    try {
      const currentRoute = findRouteByPath(routes, req.url);
      console.log(`🎯 Ruta solicitada: ${req.url}, Encontrada:`, currentRoute?.path);

      if (currentRoute) {
        // Redirigir a Vite dev server para desarrollo
        res.redirect(`http://localhost:5173${req.url}`);
      } else {
        // Mostrar mensaje de redirección
        res.status(404).send(`
          <h1>Ruta no encontrada</h1>
          <p>Redirigiendo a Vite dev server...</p>
          <script>setTimeout(() => window.location.href = 'http://localhost:5173', 1000);</script>
        `);
      }
    } catch (error) {
      console.error('❌ Error procesando ruta:', error);
      res.status(500).send(`
        <h1>Error del servidor</h1>
        <p>${error instanceof Error ? error.message : String(error)}</p>
        <a href="/">Volver al inicio</a>
      `);
    }
  });

  // Iniciar servidor
  const server = app.listen(port, host, () => {
    console.log(`✅ Servidor de desarrollo ejecutándose en http://${host}:${port}`);
    console.log(`🌐 Abre tu navegador en: http://${host}:${port}`);
    console.log(`📋 Rutas disponibles:`);
    routes.forEach(route => {
      console.log(`   - ${route.path} (${route.component})`);
    });
    console.log(`\n💡 Nota: Las rutas redirigen a Vite dev server en puerto 5173`);
  });

  // Manejo de errores del servidor
  server.on('error', (error: any) => {
    if (error.code === 'EADDRINUSE') {
      console.error(`❌ Puerto ${port} está en uso. Intenta con otro puerto.`);
      process.exit(1);
    } else {
      console.error('❌ Error del servidor:', error);
      process.exit(1);
    }
  });

  // Mantener el proceso vivo y manejar cierre
  process.on('SIGINT', () => {
    console.log('\n🛑 Cerrando servidor...');
    server.close(() => {
      console.log('✅ Servidor cerrado correctamente');
      process.exit(0);
    });
  });

  process.on('SIGTERM', () => {
    console.log('\n🛑 Cerrando servidor...');
    server.close(() => {
      console.log('✅ Servidor cerrado correctamente');
      process.exit(0);
    });
  });
}

// Funciones de escaneo síncrono
function scanRoutesSync(appDir: string): any[] {
  const routes: any[] = [];

  function scanDirectory(dir: string, basePath: string = '') {
    try {
      const files = readdirSync(dir);

      for (const file of files) {
        const fullPath = join(dir, file);
        const stat = statSync(fullPath);

        if (stat.isDirectory()) {
          // Recursivamente escanear subdirectorios
          scanDirectory(fullPath, join(basePath, file));
        } else {
          // Procesar archivo
          const ext = extname(file);
          if (['.tsx', '.jsx', '.ts', '.js'].includes(ext)) {
            const relativePath = join(basePath, file).replace(/\\/g, '/');
            const route = fileToRoute(relativePath, fullPath);
            if (route) {
              routes.push(route);
            }
          }
        }
      }
    } catch (error) {
      console.error(`Error escaneando directorio ${dir}:`, error);
    }
  }

  scanDirectory(appDir);
  return routes;
}

function fileToRoute(relativePath: string, fullPath: string): any | null {
  // Ignorar archivos que empiezan con _
  if (relativePath.includes('/_') || relativePath.startsWith('_')) {
    return null;
  }

  let path = relativePath
    .replace(/\.(tsx|jsx|ts|js)$/, '')
    .replace(/\\/g, '/');

  // Manejar index files
  if (path.endsWith('/index')) {
    path = path.replace('/index', '');
  }

  // Convertir a ruta de URL
  if (path === 'index') {
    path = '/';
  } else {
    path = '/' + path;
  }

  // Detectar rutas dinámicas
  const isDynamic = path.includes('[') && path.includes(']');

  return {
    path,
    component: fullPath,
    isDynamic,
    title: generateTitle(path)
  };
}

function findRouteByPath(routes: any[], path: string): any | null {
  // Buscar ruta exacta primero
  let route = routes.find(r => r.path === path);
  if (route) return route;

  // Buscar rutas dinámicas
  for (const r of routes) {
    if (r.isDynamic && matchesDynamicRoute(r.path, path)) {
      return r;
    }
  }

  return null;
}

function matchesDynamicRoute(routePath: string, actualPath: string): boolean {
  const routeSegments = routePath.split('/');
  const actualSegments = actualPath.split('/');

  if (routeSegments.length !== actualSegments.length) {
    return false;
  }

  for (let i = 0; i < routeSegments.length; i++) {
    const routeSegment = routeSegments[i];
    const actualSegment = actualSegments[i];

    if (routeSegment.startsWith('[') && routeSegment.endsWith(']')) {
      // Es un parámetro dinámico
      continue;
    }

    if (routeSegment !== actualSegment) {
      return false;
    }
  }

  return true;
}

function generateTitle(path: string): string {
  if (path === '/') return 'Home';

  return path
    .split('/')
    .filter(Boolean)
    .map(segment => {
      if (segment.startsWith('[') && segment.endsWith(']')) {
        return segment.slice(1, -1);
      }
      return segment.charAt(0).toUpperCase() + segment.slice(1);
    })
    .join(' ');
}